import Layout from "../../components/Layout/Layout";
import { useState, useEffect } from "react";
import { HollowDotsSpinner } from "react-epic-spinners";
import BackButton from "../../components/BackButton";
import { useNavigate } from "react-router-dom";
import ThirdPartyServices from "../../services/ThirdParty";
import fire from "../../utils/Toastr";
import { PlusCircleIcon } from "@heroicons/react/outline";
import CreateApplicationModal from "./CreateApplicationModal";

export default function CreateOrganization() {
  const [formData, setFormData] = useState({
    applicationName: "",
    companyName: "",
    companyUniqueIdentifier: "",
    address: "",
    accountNumber: "",
    tenantid: ""
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState({});

  const [applications, setApplications] = useState([]);
  const [applicationsLoading, setApplicationsLoading] = useState(false);
  const [showCreateAppModal, setShowCreateAppModal] = useState(false);
  const [selectedAppId, setSelectedAppId] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    setApplicationsLoading(true);
    try {
      const response = await ThirdPartyServices.listApplications(1, 100);
      if (response.status && response.data?.result) {
        setApplications(response.data.result);
      }
    } catch (err) {
      console.error("Error fetching applications:", err);
    } finally {
      setApplicationsLoading(false);
    }
  };

  const handleApplicationSelect = (e) => {
    const appId = e.target.value;
    setSelectedAppId(appId);

    const app = applications.find(a => a.applicationId === appId);
    if (app) {
      setFormData(prev => ({
        ...prev,
        applicationName: app.applicationName,
        tenantid: app.tenantId
      }));
      if (validationErrors.applicationName) {
        setValidationErrors(prev => ({ ...prev, applicationName: "" }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        applicationName: "",
        tenantid: ""
      }));
    }
  };

  const handleApplicationCreated = (keys) => {
    setShowCreateAppModal(false);
    fetchApplications();
    fire("success", "Application created successfully! Select it from the dropdown.");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const validateForm = () => {
    let errors = {};
    const { applicationName, companyName, companyUniqueIdentifier, address, accountNumber } = formData;

    if (!applicationName.trim()) errors.applicationName = "Application Name is required";
    if (!companyName.trim()) errors.companyName = "Company Name is required";
    if (!companyUniqueIdentifier.trim()) errors.companyUniqueIdentifier = "Company Unique Identifier is required";
    if (!address.trim()) errors.address = "Address is required";
    if (!accountNumber.trim()) errors.accountNumber = "Account Number is required";
    else if (!/^\d+$/.test(accountNumber.trim())) errors.accountNumber = "Account Number must be numeric";

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      fire("error", "Please fix the validation errors");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const createResponse = await ThirdPartyServices.createOrganization({
        applicationName: formData.applicationName.trim(),
        companyName: formData.companyName.trim(),
        companyUniqueIdentifier: formData.companyUniqueIdentifier.trim(),
        address: formData.address.trim(),
        tenantid: formData.tenantid.trim()
      });

      if (createResponse.status && createResponse.data) {
        const organizationId = createResponse.data.organizationId;
        
        const onboardResponse = await ThirdPartyServices.onboardOrganization({
          organizationId: organizationId,
          accountNumber: formData.accountNumber.trim(),
          applicationName: formData.applicationName.trim(),
          tenantId: formData.tenantid.trim()
        });

        if (onboardResponse.status) {
          fire("success", "Organization created and onboarded successfully!");
          
          try {
            await ThirdPartyServices.sendAccountNotification({
              applicationName: formData.applicationName.trim(),
              companyName: formData.companyName.trim(),
              companyUniqueIdentifier: formData.companyUniqueIdentifier.trim(),
              address: formData.address.trim(),
              status: "Success",
              accountNumber: formData.accountNumber.trim(),
              reason: "Organization successfully created and onboarded"
            });
          } catch (notificationError) {
            console.warn("Notification failed but organization was created:", notificationError);
          }

          setTimeout(() => {
            navigate("/thirdparty");
          }, 1500);
        } else {
          setError(onboardResponse.message || "Onboarding failed after creation");
          fire("error", "Organization created but onboarding failed");
        }
      } else {
        setError(createResponse.message || "Failed to create organization");
        fire("error", "Failed to create organization");
      }
    } catch (err) {
      setError("An error occurred while processing your request");
      fire("error", "An error occurred while processing your request");
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout
      title="Add Third-Party Organization"
      pageTag={"thirdparty"}
      current={"/create/thirdparty"}
    >
      <main>
        <div className="max-w-7xl mx-auto">
          <BackButton />
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="mt-8 flex flex-col">
              <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                  <div className="overflow-hidden ring-1 ring-black ring-opacity-5 md:rounded-lg bg-white mt-5">
                    <div className="p-6">
                      <h2 className="text-lg font-medium text-gray-900 mb-6">Create New Organization</h2>
                      
                      <form onSubmit={handleSubmit}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                          {/* Application Select */}
                          <div>
                            <label htmlFor="applicationSelect" className="block text-sm font-medium text-gray-700">
                              Application *
                            </label>
                            <div className="mt-1 flex rounded-md shadow-sm">
                              <select
                                id="applicationSelect"
                                value={selectedAppId}
                                onChange={handleApplicationSelect}
                                disabled={applicationsLoading}
                                className={`block w-full rounded-md shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm ${
                                  validationErrors.applicationName 
                                    ? 'border-red-300' 
                                    : 'border-gray-300'
                                } ${applicationsLoading ? 'opacity-50' : ''}`}
                              >
                                <option value="">-- Select an application --</option>
                                {applications.map(app => (
                                  <option key={app.applicationId} value={app.applicationId}>
                                    {app.applicationName}
                                  </option>
                                ))}
                              </select>
                            </div>
                            {applicationsLoading && (
                              <p className="mt-1 text-xs text-gray-500">Loading applications...</p>
                            )}
                            {validationErrors.applicationName && (
                              <p className="mt-1 text-sm text-red-600">{validationErrors.applicationName}</p>
                            )}
                          </div>

                          {/* Create New Application Button */}
                          <div className="flex items-end">
                            <button
                              type="button"
                              onClick={() => setShowCreateAppModal(true)}
                              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            >
                              <PlusCircleIcon className="w-4 mr-1.5" />
                              New Application
                            </button>
                          </div>

                          {/* Company Name */}
                          <div>
                            <label htmlFor="companyName" className="block text-sm font-medium text-gray-700">
                              Company Name *
                            </label>
                            <input
                              type="text"
                              name="companyName"
                              id="companyName"
                              value={formData.companyName}
                              onChange={handleChange}
                              className={`mt-1 block w-full rounded-md shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm ${
                                validationErrors.companyName 
                                  ? 'border-red-300' 
                                  : 'border-gray-300'
                              }`}
                              placeholder="Enter company name"
                            />
                            {validationErrors.companyName && (
                              <p className="mt-1 text-sm text-red-600">{validationErrors.companyName}</p>
                            )}
                          </div>

                          {/* Company Unique Identifier */}
                          <div>
                            <label htmlFor="companyUniqueIdentifier" className="block text-sm font-medium text-gray-700">
                              Company Unique Identifier *
                            </label>
                            <input
                              type="text"
                              name="companyUniqueIdentifier"
                              id="companyUniqueIdentifier"
                              value={formData.companyUniqueIdentifier}
                              onChange={handleChange}
                              className={`mt-1 block w-full rounded-md shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm ${
                                validationErrors.companyUniqueIdentifier 
                                  ? 'border-red-300' 
                                  : 'border-gray-300'
                              }`}
                              placeholder="Enter unique identifier"
                            />
                            {validationErrors.companyUniqueIdentifier && (
                              <p className="mt-1 text-sm text-red-600">{validationErrors.companyUniqueIdentifier}</p>
                            )}
                          </div>

                          {/* Account Number */}
                          <div>
                            <label htmlFor="accountNumber" className="block text-sm font-medium text-gray-700">
                              Account Number *
                            </label>
                            <input
                              type="text"
                              name="accountNumber"
                              id="accountNumber"
                              value={formData.accountNumber}
                              onChange={handleChange}
                              className={`mt-1 block w-full rounded-md shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm ${
                                validationErrors.accountNumber 
                                  ? 'border-red-300' 
                                  : 'border-gray-300'
                              }`}
                              placeholder="Enter account number"
                            />
                            {validationErrors.accountNumber && (
                              <p className="mt-1 text-sm text-red-600">{validationErrors.accountNumber}</p>
                            )}
                          </div>

                          {/* Address (Full width) */}
                          <div className="sm:col-span-2">
                            <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                              Address *
                            </label>
                            <textarea
                              name="address"
                              id="address"
                              rows={3}
                              value={formData.address}
                              onChange={handleChange}
                              className={`mt-1 block w-full rounded-md shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm ${
                                validationErrors.address 
                                  ? 'border-red-300' 
                                  : 'border-gray-300'
                              }`}
                              placeholder="Enter company address"
                            />
                            {validationErrors.address && (
                              <p className="mt-1 text-sm text-red-600">{validationErrors.address}</p>
                            )}
                          </div>
                        </div>

                        {error && (
                          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                            <p className="text-sm text-red-600">{error}</p>
                          </div>
                        )}

                        <div className="mt-8 flex justify-end space-x-3">
                          <button
                            type="button"
                            onClick={() => navigate("/thirdparty")}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            disabled={loading}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                          >
                            {loading ? (
                              <>
                                <HollowDotsSpinner color="#ffffff" size={20} className="mr-2" />
                                Creating...
                              </>
                            ) : (
                              'Create Organization'
                            )}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <CreateApplicationModal
        isOpen={showCreateAppModal}
        onClose={() => setShowCreateAppModal(false)}
        onSuccess={handleApplicationCreated}
      />
    </Layout>
  );
}
